import * as Sentry from '@sentry/nextjs';
import {
  Alert,
  AlertIcon,
  Button,
  HStack,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Minus, Plus } from '@phosphor-icons/react';
import { useReCaptcha } from 'next-recaptcha-v3';
import FieldWrapper from '@/components/common/FieldWrapper';
import ChakraSelect from '@/components/common/ChakraSelect';
import SalesOrderFormSchema, { SalesOrderFormSchemaType } from './schema';
import {
  OptionProp,
  optionPropFormat,
  optionPropParse,
} from '@/lib/optionProps';
import useValidationSchema from '@/lib/hooks/useValidationSchema';
import Heading from '../Heading';
import { useInsertSalesOrderMutation } from '@/generated/graphql';

export interface SalesOrderFormProps {
  data: {
    municipalityOptions?: OptionProp<string>[];
    propertyTypes?: string[];
  };
}

const STACK_SPACING = 6;

const SalesOrderForm = ({
  data: { municipalityOptions = [], propertyTypes = [] },
}: SalesOrderFormProps) => {
  const { t } = useTranslation(['frontoffice', 'backoffice']);
  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const [insert] = useInsertSalesOrderMutation();
  const [success, setSuccess] = useState(false);
  const { executeRecaptcha, loaded } = useReCaptcha();

  const formError = {
    [FORM_ERROR]: t(
      'sales_order_submission_error',
      'Myyntitoimeksiantoa ei voitu tallentaa, yritä uudelleen tai ota suoraan yhteyttä alueesi välitysliikkeeseen.',
    ),
  };

  const onSubmit = async (values: SalesOrderFormSchemaType) => {
    try {
      let token = '';
      if (loaded) {
        token = await executeRecaptcha('insert_sales_order');
      }
      const { data, errors } = await insert({
        variables: {
          salesOrder: {
            ...values,
            area: values.area?.toString(),
            forest_area: values.forest_area?.toString(),
          },
        },
        context: {
          headers: {
            'x-captcha-token': token,
          },
        },
      });

      const affectedRows = data?.insert_sales_order?.affected_rows || 0;

      if (errors) {
        throw errors;
      }

      if (affectedRows < 1) {
        return formError;
      }

      return setSuccess(true);
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      return formError;
    }
  };

  const validate = useValidationSchema(SalesOrderFormSchema);

  const propertyTypeOptions: OptionProp<string>[] = propertyTypes.map(
    (value) => ({
      value,
      label: t(`backoffice:property_type_values.${value}`),
    }),
  );

  if (success) {
    return (
      <Stack spacing={STACK_SPACING} textAlign="center" pt={14}>
        <Heading as="h3" variant="subtitle">
          {t('sales_order_thank_you_title', 'Kiitos yhteydenotostanne!')}
        </Heading>
        <Text>
          {t(
            'sales_order_thank_you_text',
            'Olemme sinuun yhteydessä mahdollisimman pian!',
          )}
        </Text>
      </Stack>
    );
  }

  return (
    <Form<SalesOrderFormSchemaType> onSubmit={onSubmit} validate={validate}>
      {({ handleSubmit, hasValidationErrors, submitError }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Stack spacing={STACK_SPACING}>
            <Heading as="h3" variant="subtitle">
              {t('contact_information', 'Yhteystiedot')}
            </Heading>
            <FieldWrapper name="name" label={t('name', 'Nimi')} isRequired>
              {({ input }) => (
                <Input {...input} placeholder={t('name', 'Nimi')} />
              )}
            </FieldWrapper>
            <FieldWrapper
              name="address"
              label={t('street_address', 'Lähiosoite')}
              isRequired
            >
              {({ input }) => (
                <Input
                  {...input}
                  placeholder={t('street_address', 'Lähiosoite')}
                />
              )}
            </FieldWrapper>
            <HStack>
              <FieldWrapper
                name="postal_code"
                label={t('postal_code', 'Postinumero')}
                isRequired
              >
                {({ input }) => (
                  <Input
                    {...input}
                    placeholder={t('postal_code_placeholder', '00100')}
                  />
                )}
              </FieldWrapper>
              <FieldWrapper
                name="city"
                label={t('city', 'Postitoimipaikka')}
                isRequired
              >
                {({ input }) => (
                  <Input
                    {...input}
                    placeholder={t('city', 'Postitoimipaikka')}
                  />
                )}
              </FieldWrapper>
            </HStack>
            <HStack>
              <FieldWrapper
                name="email"
                label={t('email', 'Sähköpostiosoite')}
                isRequired
              >
                {({ input }) => (
                  <Input
                    {...input}
                    placeholder={t('email', 'Sähköpostiosoite')}
                  />
                )}
              </FieldWrapper>
              <FieldWrapper
                name="phone_number"
                label={t('phone_number', 'Puhelin')}
                isRequired
              >
                {({ input }) => (
                  <Input
                    {...input}
                    placeholder={t(
                      'phone_number_placeholder',
                      '+358 50 123 4567',
                    )}
                  />
                )}
              </FieldWrapper>
            </HStack>

            <Heading as="h3" variant="subtitle" py={5}>
              {t('listing_information', 'Kohteen tiedot')}
            </Heading>

            <FieldWrapper
              name="property_municipality"
              label={t('municipality', 'Kunta')}
              parse={optionPropParse<string>}
              format={optionPropFormat<string>(municipalityOptions)}
              isRequired
            >
              {({ input }) => (
                <ChakraSelect {...input} options={municipalityOptions} />
              )}
            </FieldWrapper>

            <FieldWrapper
              name="property_description"
              label={t('listing_description', 'Kuvaus kohteesta')}
            >
              {({ input }) => (
                <Textarea
                  {...input}
                  placeholder={t(
                    'listing_description_placeholder',
                    'Kerro myytävästä kohteesta vapaasti tai täydennä tarkemmat tiedot kohdasta Lisätiedot.',
                  )}
                />
              )}
            </FieldWrapper>
          </Stack>

          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            iconSpacing="2"
            leftIcon={showExtraInfo ? <Minus /> : <Plus />}
            onClick={() => {
              setShowExtraInfo(!showExtraInfo);
            }}
            mt={8}
          >
            {showExtraInfo
              ? t('close_extra_information', 'Sulje lisätiedot')
              : t('extra_information', 'Lisätiedot')}
          </Button>
          {showExtraInfo && (
            <Stack spacing={STACK_SPACING}>
              <Heading as="h3" variant="subtitle" py={6}>
                {t('extra_information', 'Lisätiedot')}
              </Heading>
              <HStack>
                <FieldWrapper
                  name="property_id"
                  label={t('property_id', 'Kiinteistötunnus')}
                >
                  {({ input }) => (
                    <Input
                      {...input}
                      placeholder={t(
                        'property_id_placeholder',
                        'Esimerkki 12-234-5-678',
                      )}
                    />
                  )}
                </FieldWrapper>
                <FieldWrapper
                  name="property_type"
                  label={t('property_type', 'Kiinteistötyyppi')}
                  parse={optionPropParse<string>}
                  format={optionPropFormat<string>(propertyTypeOptions)}
                >
                  {({ input }) => (
                    <ChakraSelect {...input} options={propertyTypeOptions} />
                  )}
                </FieldWrapper>
              </HStack>
              <HStack>
                <FieldWrapper
                  name="area"
                  label={t('listing_area', 'Kohteen pinta-ala (ha)')}
                >
                  {({ input }) => <Input {...input} />}
                </FieldWrapper>
                <FieldWrapper
                  name="forest_area"
                  label={t('forest_area', 'Metsän pinta-ala (ha)')}
                >
                  {({ input }) => <Input {...input} />}
                </FieldWrapper>
              </HStack>
              <HStack>
                <FieldWrapper
                  name="property_address"
                  label={t('listing_address', 'Kohteen osoite')}
                >
                  {({ input }) => <Input {...input} />}
                </FieldWrapper>
                <FieldWrapper
                  name="property_postal_code"
                  label={t('listing_postal_code', 'Kohteen postinumero')}
                >
                  {({ input }) => <Input {...input} />}
                </FieldWrapper>
              </HStack>
            </Stack>
          )}
          <HStack py={6} spacing={10}>
            <Button
              colorScheme="primary"
              type="submit"
              disabled={hasValidationErrors}
            >
              {t('send', 'Lähetä')}
            </Button>

            {submitError ? (
              <Alert status="error">
                <AlertIcon />
                {submitError}
              </Alert>
            ) : null}
          </HStack>
        </form>
      )}
    </Form>
  );
};

export default SalesOrderForm;
